// src/pages/CartPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Footer from "../components/Footer";
import MobileFooterSheet from "../components/MobileFooterSheet";
import "../styles/CartPage.css";
import "../styles/mobile-CartPage.css";
import useCartPageController from "../controllers/useCartPageController";
import CartLocationSelectors from "../components/CartLocationSelectors";
import currencyGreen from "../assets/images/currency_white_1.png";
import trash from "../assets/images/trash.png";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

export default function CartPage({ currentView, setCurrentView }) {
  const {
    // state
    items, cartId, address, payment, coupon, deletingId, confirming, cartLoading,
    guestPhone, guestLoaded, guestLoading, guestError,
    // setters
    setAddress, setPayment, setCoupon, setGuestPhone,
    // actions
    fetchGuestCart, inc, dec, handleRemove, handleConfirm,
    // computed
    partsTotal, deliveryTotalRaw, accessoriesTotal, discountTotal, deliverySurcharge, grandTotal,
    // helpers/constants for view
    money, ACCESSORY_FEE, guestReady,
  } = useCartPageController();

  const token = localStorage.getItem("token");
  const isAuthed = !!token;

  // ➜ Navigate to a separate Part Details page
  const openDetails = (partId) => {
    if (!partId) return;
    try { sessionStorage.setItem("last_part_id", String(partId)); } catch { }
    if (typeof setCurrentView === "function") setCurrentView("partDetails");
    else window.location.assign(`/part/${partId}`);
  };

  // ---------- Apple Pay support detection ----------
  const [applePaySupported, setApplePaySupported] = React.useState(false);

  React.useEffect(() => {
    try {
      // Primary: feature detection (only Safari exposes this)
      const hasAPS =
        typeof window !== "undefined" &&
        window.ApplePaySession &&
        typeof window.ApplePaySession.canMakePayments === "function";

      if (hasAPS) {
        // `canMakePayments()` is sync and safe for gating the UI
        const ok = window.ApplePaySession.canMakePayments();
        setApplePaySupported(!!ok);
        return;
      }

      // Fallback heuristics (very conservative):
      // show only on Safari UA on iOS/iPadOS or macOS
      const ua = navigator.userAgent || "";
      const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isMac = /Macintosh/.test(ua);
      const isSafari = /^((?!chrome|crios|fxios|edg|edge|opr|brave|vivaldi).)*safari/i.test(ua);
      setApplePaySupported(isSafari && (isIOS || isMac));
    } catch {
      setApplePaySupported(false);
    }
  }, []);
  // -------------------------------------------------

  const [isLaptop, setIsLaptop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      setIsLaptop(w >= 1024);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Build payment methods list, hiding Apple Pay when unsupported
  const paymentMethods = React.useMemo(
    () =>
      [
        { value: "apple", label: "Apple Pay", requiresApple: true },
        { value: "mada", label: "مدى" },
        { value: "visa", label: "فيزا" },
        { value: "tabby", label: "Tabby" },
        { value: "tamara", label: "تمارا" },
      ].filter((m) => !(m.requiresApple && !applePaySupported)),
    [applePaySupported]
  );

  // If currently selected method became hidden (e.g., switching browser), fix it
  React.useEffect(() => {
    const stillVisible = paymentMethods.some((m) => m.value === payment);
    if (!stillVisible && paymentMethods.length) {
      setPayment(paymentMethods[0].value);
    }
  }, [paymentMethods, payment, setPayment]);

  const formRef = React.useRef(null);

  // Small wrapper to keep controller untouched
  const onSubmit = (e) => {

    // Always stop the real submit while in maintenance
    // e.preventDefault();
    // e.stopPropagation();


    // Swal.fire({
    //   title: '⚠️ تنبيه',
    //   text: 'الموقع تحت الصيانة، راح نرجعلكم خلال ساعات',
    //   icon: 'info',
    //   confirmButtonText: "حسناً",
    //   confirmButtonColor: "#CAEA3E",
    //   iconColor: "#CAEA3E",
    //   customClass: {
    //     popup: "swal-dark-popup",
    //     cancelButton: "swal-cancel-btn",
    //   },
    // });

    // return;

    const form = e.currentTarget;

    // ✅ طبّق رسائل مخصّصة لكل الحقول قبل الفحص
    const fields = form.querySelectorAll('input, select, textarea');
    fields.forEach((el) => setCustomMessage(el));

    if (!form.checkValidity()) {
      e.preventDefault();
      e.stopPropagation();
      form.classList.add("was-validated");

      // امسح العلامات السابقة
      form.querySelectorAll(".is-invalid").forEach(el => el.classList.remove("is-invalid"));

      // علّم الحقول غير الصالحة
      const invalids = form.querySelectorAll("input:invalid, select:invalid, textarea:invalid");
      invalids.forEach((el) => {
        el.classList.add("is-invalid");
        const wrap = el.closest(".addr-input-wrap, .ss-control, .addr-field");
        if (wrap) wrap.classList.add("is-invalid");
      });

      form.reportValidity(); // سيعرض رسائل setCustomValidity
      return;
    }

    // صالح → تابع
    handleConfirm(e);
  };


  React.useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const clearInvalid = (el) => {
      el.classList.remove('is-invalid');
      const wrap = el.closest('.addr-input-wrap, .ss-control, .addr-field');
      if (wrap) wrap.classList.remove('is-invalid');
    };

    const handleFieldChange = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) return;

      // ✅ اضبط الرسالة المخصّصة مع كل تغيير
      setCustomMessage(el);

      // إذا صار صالحًا الآن → أزل is-invalid مباشرة
      if (el.checkValidity()) {
        clearInvalid(el);
      }
    };

    form.addEventListener('input', handleFieldChange);
    form.addEventListener('change', handleFieldChange);
    return () => {
      form.removeEventListener('input', handleFieldChange);
      form.removeEventListener('change', handleFieldChange);
    };
  }, []);



  function validateFullNameValue(v) {
    if (!v) return false;
    const parts = v.trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return false;
    return parts[0].length >= 2 && parts[1].length >= 2;
  }

  // ✅ 1) دالة مساعدة: رسالة التحقق لكل الحقول
  function setCustomMessage(el) {
    // اسم الحقل (لصياغة رسالة ألطف) — خذ من data-label أو aria-label أو name
    const label =
      el.getAttribute?.('data-label') ||
      el.getAttribute?.('aria-label') ||
      el.name ||
      'هذا الحقل';

    // ابدأ بإزالة أي رسالة سابقة
    el.setCustomValidity('');

    // اسم كامل مخصّص
    if (el.name === 'fullName') {
      if (!validateFullNameValue(el.value)) {
        el.setCustomValidity('الاسم يجب أن يحتوي على جزئين على الأقل');
        return;
      }
    }

    // استخدم كائن validity القياسي لرسائل عامة
    const v = el.validity;
    if (v.valueMissing) {
      // للـ select أعطِ صياغة ألطف
      if (el instanceof HTMLSelectElement) {
        el.setCustomValidity(`الرجاء اختيار ${label}`);
      } else {
        el.setCustomValidity(` ${label} مطلوب`);
      }
    } else if (v.typeMismatch) {
      el.setCustomValidity(`الرجاء إدخال ${label} بصيغة صحيحة`);
    } else if (v.patternMismatch) {
      el.setCustomValidity(`القيمة المدخلة لا تطابق التنسيق المطلوب لـ ${label}`);
    } else if (v.tooShort) {
      el.setCustomValidity(`الحد الأدنى لطول ${label} هو ${el.minLength} حروف`);
    } else if (v.tooLong) {
      el.setCustomValidity(`الحد الأقصى لطول ${label} هو ${el.maxLength} حروف`);
    } else if (v.rangeUnderflow) {
      el.setCustomValidity(`قيمة ${label} أقل من المسموح (${el.min})`);
    } else if (v.rangeOverflow) {
      el.setCustomValidity(`قيمة ${label} أكبر من المسموح (${el.max})`);
    } else if (v.stepMismatch) {
      el.setCustomValidity(`قيمة ${label} غير متوافقة مع التدرّج المحدد`);
    }
  }


  return (
    <div className="cart-page" dir="rtl">
      <div className="clip-bg cart" />

      <div className="cart-wrapper">
        <div className="container cart-container">
          <h1 className="cart-title">سلة التسوق</h1>
          {isLaptop && <motion.button
            className="continue-pill"
            onClick={() => setCurrentView && setCurrentView("brands")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            style={{ position: "absolute", height: 40, top: 30, left: 80 }}
          >
            <span>تابع التسوق</span>
          </motion.button>}

          <form ref={formRef} noValidate onSubmit={onSubmit}>
            {/* Items table */}
            <div className="cart-table">
              <div className="cart-row cart-head">
                <div className="col-product">المنتج</div>
                <div className="col-price">السعر</div>
                <div className="col-qty">الكمية</div>
                <div className="col-ship">التوصيل</div>
                <div className="col-total">السعر الإجمالي</div>
                <div className="col-del" aria-hidden />
              </div>

              {cartLoading ? (
                <div className="cart-row">
                  <div className="col-product">يتم جلب محتويات السلة...</div>
                </div>
              ) : items.length === 0 ? (
                <div className="cart-row">
                  <div className="col-product">لا توجد عناصر في السلة.</div>
                </div>
              ) : (
                items.map((it) => {
                  const showDiscount = it.unitDiscount > 0;
                  const isDeleting = deletingId === it.id;
                  const lineDelivery = Math.min(it.unitDelivery * it.qty, 200);
                  const lineTotal = it.unitPrice * it.qty + lineDelivery;

                  const atLimit = Number.isFinite(it.availableQty) && it.qty >= it.availableQty;
                  const plusDisabled = isDeleting || atLimit;
                  const availTitle = Number.isFinite(it.availableQty) ? `المتوفر: ${it.availableQty}` : undefined;

                  return (
                    <div
                      key={it.id}
                      className="cart-item-wrap"
                      aria-busy={isDeleting}
                      role="button"
                      tabIndex={0}
                      onClick={() => !isDeleting && openDetails(it.partId)}
                      onKeyDown={(e) => {
                        if (isDeleting) return;
                        if (e.key === "Enter" || e.key === " ") openDetails(it.partId);
                      }}
                      style={{ cursor: isDeleting ? "default" : "pointer" }}
                    >
                      <div
                        className="cart-row cart-item"
                        style={isDeleting ? { opacity: 0.6, pointerEvents: "none" } : undefined}
                      >
                        <div className="col-product" style={{ justifyContent: "right" }}>
                          <img src={it.img} alt={it.name || "part"} className="p-thumb" />
                          <span className="p-name">{it.name}</span>
                        </div>

                        <div className="col-price">

                          <div className="price-div" style={{ display: "flex", gap: 8, alignItems: "baseline", textAlign: "center", justifyContent: "center" }}>
                            <span style={{ textDecoration: "line-through", opacity: 0.7, fontSize: 22 }}>
                              {money(it.unitPriceOriginal)}
                            </span>
                            <b style={{ textDecoration: "none", color: "white" }}>{money(it.unitPrice)}</b>
                          </div>

                        </div>

                        <div className="col-qty">
                          <>
                            <button
                              className="qty-btn"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); inc(it.id); }}
                              disabled={plusDisabled}
                              title={availTitle}
                            >
                              +
                            </button>
                            <span className="qty-value">{it.qty}</span>
                            <button
                              className="qty-btn"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); dec(it.id); }}
                              disabled={isDeleting}
                            >
                              -
                            </button>
                          </>
                        </div>

                        <div className="col-ship">{money(lineDelivery)}  <img src={currencyGreen} alt="" /> </div>

                        <div className="col-total" style={{ color: "#caff0a" }}>
                          {money(lineTotal)} <img src={currencyGreen} alt="" />
                        </div>

                        <div className="col-del">
                          <button
                            className="trash"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemove(it.id); }}
                            aria-label="حذف"
                            disabled={isDeleting}
                            title={isDeleting ? "..." : "حذف"}
                          >
                            {isDeleting ? "…" : <img src={trash} alt="" width={"15px"} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Address + Contact */}
            <h3 style={{ marginTop: 30 }}>اختر موقعك</h3>
            <CartLocationSelectors value={address} onChange={setAddress} />

            {/* Summary */}
            <h3 style={{ marginTop: 20 }}>ملخص الطلب</h3>
            <div className="summary-wide" dir="rtl">
              <div className="sum-row">
                <span className="sum-label">إجمالي قطع الغيار</span>
                <span className="sum-value">{money(partsTotal)} <img src={currencyGreen} alt="" /></span>
              </div>
              <div className="sum-row">
                <span className="sum-label">تكلفة التوصيل</span>
                <span className="sum-value">
                  {money(deliveryTotalRaw)}
                  {deliverySurcharge > 0 && (
                    <small style={{ opacity: 0.8, marginInlineStart: 8 }}>
                      (+{money(deliverySurcharge)} خارج الرياض)
                    </small>
                  )}
                  <img src={currencyGreen} alt="" />
                </span>
              </div>
              {/* <div className="sum-row">
              <span className="sum-label">ملحقات</span>
              <span className="sum-value">{money(accessoriesTotal)}</span>
            </div> */}
              <div className="sum-row">
                <span className="sum-label">الخصم</span>
                <span className="sum-value">{money(discountTotal)} <img src={currencyGreen} alt="" /></span>
              </div>
              <div className="sum-mid">
                <span className="dash" aria-hidden />
                <input
                  type="text"
                  className="coupon-input"
                  placeholder="أدخل كوبون الخصم / كيف سمعت عنا؟"
                  aria-label="كوبون الخصم"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                />
              </div>
              <div className="sum-row sum-total">
                <span className="sum-label">المجموع النهائي</span>
                <span className="sum-value">{money(grandTotal)} <img src={currencyGreen} alt="" /></span>
              </div>
            </div>

            {/* Payment */}
            {/* <div className="payment">
            <h3>اختر طريقة الدفع</h3>
            <div className="pay-bar" dir="rtl">
              {paymentMethods.map((opt) => (
                <label key={opt.value} className={`pay-option ${payment === opt.value ? "checked" : ""}`}>
                  <span className="label-text">{opt.label}</span>
                  <input
                    type="radio"
                    name="pay"
                    value={opt.value}
                    checked={payment === opt.value}
                    onChange={(e) => setPayment(e.target.value)}
                  />
                </label>
              ))}
            </div>
          </div> */}

            <div className="actions">
              <button
                className="confirm-btn"
                type="submit"
              // disabled={
              //   confirming ||
              //   cartLoading ||
              //   items.length === 0 ||
              //   (!isAuthed && !guestReady)
              // }

              >
                {confirming ? "..." : "تأكيد الطلبية"}
              </button>
            </div>

          </form>
        </div>
      </div>

      <div className="desktop-only">
        <Footer setCurrentView={setCurrentView} />
      </div>

      <div className="hero">
        <MobileFooterSheet />
      </div>
    </div>
  );
}
